import { useEffect } from "react";
import { useHead as useUnhead } from "@unhead/react";
import type { JsonLdObject, WithContext } from "schema-dts";
import { useContent } from "./useContent";
import { getGermanUrl, getLocalizedUrl } from "../utils/url-slug-mapping";
import { isGermanOnlyPath } from "../utils/german-only-paths";

type UseHeadOptions = {
    title?: string;
    description?: string;
    keywords?: string;
    ogImage?: string | null;
    ogType?: string;
    canonical?: string;
    noIndex?: boolean;
    // One or more Schema.org objects, emitted as <script type="application/ld+json">.
    // During SSG these land in the static HTML that crawlers and AI engines read.
    jsonLd?: WithContext<JsonLdObject> | WithContext<JsonLdObject>[];
};

export const useHead = (options: UseHeadOptions = {}) => {
    const { language } = useContent();
    const {
        title = "Quatico - Composable Digital Services Platform",
        // Language-aware fallback so a page that omits its own description does
        // not leak German onto an English page.
        description = language === "en"
            ? "Composable Digital Services Platform for Swiss SMEs"
            : "Composable Digital Services Platform für KMU",
        keywords,
        ogImage = "/images/og-default.jpg",
        ogType = "website",
        canonical,
        noIndex = false,
        jsonLd,
    } = options;

    const jsonLdScripts = (jsonLd ? (Array.isArray(jsonLd) ? jsonLd : [jsonLd]) : []).map((schema, index) => {
        const schemaType = schema["@type"];
        return {
            type: "application/ld+json",
            // Stable, lowercase key so unhead replaces (not duplicates) the block on
            // client navigation. Lowercase keeps page-level keys in the same namespace
            // as GlobalStructuredData (ld-organization, ld-website): a page that ever
            // emits its own Organization schema then replaces the global block instead
            // of producing a duplicate entity.
            key: `ld-${typeof schemaType === "string" ? schemaType.toLowerCase() : index}`,
            innerHTML: JSON.stringify(schema),
        };
    });

    const baseUrl = "https://www.quatico.com";

    // Derive the German canonical path for hreflang construction.
    // Pages pass location.pathname which may be localized (e.g., "/en/products/sales-portal").
    // We need the German equivalent to generate correct hreflang URLs for all languages.
    const germanCanonical = canonical ? getGermanUrl(canonical, language) : "";
    const currentUrl = canonical ? `${baseUrl}${canonical}` : baseUrl;

    const languageMap: Record<string, string> = {
        de: "de-CH",
        en: "en",
    };

    const htmlLang = languageMap[language] || "de-CH";

    useEffect(() => {
        if (typeof document !== "undefined") {
            document.documentElement.lang = htmlLang;
        }
    }, [htmlLang]);

    useUnhead({
        htmlAttrs: { lang: htmlLang },
        title,
        meta: [
            { name: "description", content: description },
            ...(keywords ? [{ name: "keywords", content: keywords }] : []),
            ...(noIndex ? [{ name: "robots", content: "noindex, nofollow" }] : []),
            { property: "og:title", content: title },
            { property: "og:description", content: description },
            { property: "og:type", content: ogType },
            { property: "og:url", content: currentUrl },
            { property: "og:image", content: `${baseUrl}${ogImage}` },
            { property: "og:locale", content: htmlLang },
            { name: "twitter:card", content: "summary_large_image" },
            { name: "twitter:title", content: title },
            { name: "twitter:description", content: description },
            { name: "twitter:image", content: `${baseUrl}${ogImage}` },
        ],
        link: [
            ...(canonical ? [{ rel: "canonical", href: currentUrl }] : []),
            { rel: "alternate", hreflang: "de-CH", href: `${baseUrl}${germanCanonical}` },
            // A German-only page (`german-only-paths.ts`) has no EN twin; an
            // `en` alternate would point search engines at a 404.
            ...(isGermanOnlyPath(germanCanonical)
                ? []
                : [{ rel: "alternate", hreflang: "en", href: `${baseUrl}${getLocalizedUrl(germanCanonical, "en")}` }]),
            { rel: "alternate", hreflang: "x-default", href: `${baseUrl}${germanCanonical}` },
        ],
        ...(jsonLdScripts.length > 0 ? { script: jsonLdScripts } : {}),
    });

    return null;
};
